import { useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useCsDashboard } from '../hooks/useCsInquiry.js';
import { CsDailyDetailTable } from '../components/cs-inquiry/CsDailyDetailTable.js';
import { CsInquiryRateChart } from '../components/cs-inquiry/CsInquiryRateChart.js';
import { UnfetchedChatsBanner } from '../components/cs-inquiry/UnfetchedChatsBanner.js';
import {
  CHART_COLORS,
  TOOLTIP_CONTENT_STYLE,
  AXIS_TICK_STYLE,
  GRID_PROPS,
} from '../lib/chart-theme.js';
import { DateRangeFilter } from '../components/chart/DateRangeFilter.js';

/**
 * @graph-connects none
 */
const DAY_LABEL_KEYS: Record<number, string> = {
  0: 'csInquiry.daySun',
  1: 'csInquiry.dayMon',
  2: 'csInquiry.dayTue',
  3: 'csInquiry.dayWed',
  4: 'csInquiry.dayThu',
  5: 'csInquiry.dayFri',
  6: 'csInquiry.daySat',
};

/**
 * @graph-connects none
 */
interface HeatmapRow {
  day: string;
  hours: number[];
}

/**
 * @graph-connects none
 */
function formatSeconds(t: TFunction, seconds: number | null): string {
  if (seconds === null) return '-';
  if (seconds < 60) return t('csInquiry.secondsUnit', { num: String(Math.round(seconds)) });
  if (seconds < 3600) return t('csInquiry.minutesUnit', { num: String(Math.round(seconds / 60)) });
  return t('csInquiry.hoursUnit', { num: (seconds / 3600).toFixed(1) });
}

/** @graph-connects none */
export function formatNumber(v: string | number): string {
  return Number(v).toLocaleString('ja-JP');
}

/** 秒数軸・ツールチップ用の表示フォーマッタを生成する。
 * @graph-connects none
 */
export function createSecondsAxisFormatter(t: TFunction): (v: string | number) => string {
  return (v) => formatSeconds(t, Number(v));
}

/**
 * @graph-connects none
 */
function defaultFrom(): string {
  const d = new Date(Date.now() + 9 * 60 * 60 * 1000);
  d.setUTCDate(d.getUTCDate() - 30);
  return d.toISOString().slice(0, 10);
}

/**
 * @graph-connects none
 */
function defaultTo(): string {
  const d = new Date(Date.now() + 9 * 60 * 60 * 1000);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 問い合わせ分析ダッシュボード。Channel Talk データの日次推移・応答速度・時間帯分布・担当者別統計を可視化する。状態を取り込めていないチャットがあれば見出しの下に帯を出す。
 * @graph-connects useCsDashboard [calls] ダッシュボードデータ取得フック
 * @graph-connects UnfetchedChatsBanner [calls] 未取得の帯
 */
export function CsInquiryDashboardPage() {
  const { t } = useTranslation();
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const { data, isLoading, error } = useCsDashboard(from, to);

  const isEmpty = !data || data.dailySummary.length === 0;

  const latestWeek = data?.dailySummary.slice(-7) ?? [];
  const prevWeek = data?.dailySummary.slice(-14, -7) ?? [];

  const sumField = (rows: typeof latestWeek, field: keyof (typeof latestWeek)[0]) =>
    rows.reduce((s, r) => {
      const v = r[field];
      return s + (typeof v === 'number' ? v : 0);
    }, 0);

  const avgField = (rows: typeof latestWeek, field: keyof (typeof latestWeek)[0]) => {
    const vals = rows
      .map((r) => r[field])
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    return vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
  };

  const formatSecondsAxis = createSecondsAxisFormatter(t);

  const kpiCards = [
    {
      id: 'newChats',
      label: t('csInquiry.kpiNewChats'),
      current: sumField(latestWeek, 'newChats'),
      prev: sumField(prevWeek, 'newChats'),
    },
    {
      id: 'resolvedChats',
      label: t('csInquiry.kpiResolvedChats'),
      current: sumField(latestWeek, 'resolvedChats'),
      prev: sumField(prevWeek, 'resolvedChats'),
    },
    {
      id: 'avgFirstResponse',
      label: t('csInquiry.legendAvgFirstResponse'),
      current: avgField(latestWeek, 'avgFirstResponseSeconds'),
      prev: avgField(prevWeek, 'avgFirstResponseSeconds'),
      format: true,
    },
    {
      id: 'avgResolution',
      label: t('csInquiry.legendAvgResolution'),
      current: avgField(latestWeek, 'avgResolutionSeconds'),
      prev: avgField(prevWeek, 'avgResolutionSeconds'),
      format: true,
    },
  ];

  const heatmapData: HeatmapRow[] = (() => {
    if (!data) return [];
    const grid: Record<string, number> = {};
    for (const item of data.hourlyDistribution) {
      grid[`${String(item.dayOfWeek)}-${String(item.hour)}`] = item.count;
    }
    return Array.from({ length: 7 }, (_, dow): HeatmapRow => {
      const dayKey = DAY_LABEL_KEYS[dow];
      return {
        day: dayKey ? t(dayKey) : '',
        hours: Array.from({ length: 24 }, (__, h) => grid[`${String(dow + 1)}-${String(h)}`] ?? 0),
      };
    });
  })();

  const maxHeatVal = Math.max(1, ...(data?.hourlyDistribution.map((d) => d.count) ?? [1]));

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-xl font-bold text-[var(--color-text-primary)]">
          {t('csInquiry.title')}
        </h1>
        <DateRangeFilter from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
      </div>

      <UnfetchedChatsBanner page="dashboard" />

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="loading-spinner" />
        </div>
      )}

      {error && (
        <Card className="p-6 text-center text-[var(--color-error)]">{t('common.fetchError')}</Card>
      )}

      {!isLoading && !error && isEmpty && (
        <Card className="p-8 text-center">
          <p className="text-sm text-[var(--color-text-muted)]">{t('csInquiry.noData')}</p>
        </Card>
      )}

      {!isLoading && !error && !isEmpty && (
        <>
          {/* KPI カード */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {kpiCards.map((kpi) => {
              const prev = kpi.prev;
              const current = kpi.current;
              const diff =
                typeof prev === 'number' && prev !== 0 && typeof current === 'number'
                  ? ((current - prev) / prev) * 100
                  : null;
              return (
                <Card key={kpi.id} className="p-4">
                  <div className="text-xs text-[var(--color-text-muted)]">
                    {t('csInquiry.kpiLast7Days', { label: kpi.label })}
                  </div>
                  <div className="mt-1 text-2xl font-bold text-[var(--color-text-primary)]">
                    {kpi.format
                      ? formatSeconds(t, typeof current === 'number' ? current : null)
                      : t('csInquiry.chatCountUnit', {
                          num: (typeof current === 'number' ? current : 0).toLocaleString('ja-JP'),
                        })}
                  </div>
                  {diff !== null && (
                    <div
                      className={`mt-1 text-xs ${
                        kpi.format
                          ? diff <= 0
                            ? 'text-[var(--color-success)]'
                            : 'text-[var(--color-error)]'
                          : diff >= 0
                            ? 'text-[var(--color-text-muted)]'
                            : 'text-[var(--color-error)]'
                      }`}
                    >
                      {t('csInquiry.weekOverWeek', {
                        value: `${diff > 0 ? '+' : ''}${diff.toFixed(1)}`,
                      })}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>

          {/* 件数推移チャート */}
          <Card className="p-4">
            <h3 className="mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
              {t('csInquiry.trendTitle')}
            </h3>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={data.dailySummary.map((d) => ({ ...d, label: d.date.slice(5) }))}
                  margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
                >
                  <CartesianGrid {...GRID_PROPS} />
                  <XAxis dataKey="label" tick={AXIS_TICK_STYLE} tickLine={false} />
                  <YAxis
                    tick={AXIS_TICK_STYLE}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={formatNumber}
                  />
                  <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line
                    type="monotone"
                    dataKey="newChats"
                    name={t('csInquiry.legendNew')}
                    stroke={CHART_COLORS[0]}
                    dot={false}
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="resolvedChats"
                    name={t('csInquiry.legendResolved')}
                    stroke={CHART_COLORS[1]}
                    dot={false}
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="uniqueMembers"
                    name={t('csInquiry.legendUniqueUsers')}
                    stroke={CHART_COLORS[2]}
                    dot={false}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <CsInquiryRateChart rows={data.dailySummary} />

          {/* 応答速度推移 */}
          <Card className="p-4">
            <h3 className="mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
              {t('csInquiry.responseSpeedTitle')}
            </h3>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={data.dailySummary.map((d) => ({ ...d, label: d.date.slice(5) }))}
                  margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
                >
                  <CartesianGrid {...GRID_PROPS} />
                  <XAxis dataKey="label" tick={AXIS_TICK_STYLE} tickLine={false} />
                  <YAxis
                    tick={AXIS_TICK_STYLE}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={formatSecondsAxis}
                  />
                  <Tooltip
                    contentStyle={TOOLTIP_CONTENT_STYLE}
                    formatter={formatSecondsAxis as never}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line
                    type="monotone"
                    dataKey="avgFirstResponseSeconds"
                    name={t('csInquiry.legendAvgFirstResponse')}
                    stroke={CHART_COLORS[0]}
                    dot={false}
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="p50FirstResponseSeconds"
                    name={t('csInquiry.legendP50FirstResponse')}
                    stroke={CHART_COLORS[1]}
                    dot={false}
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="avgResolutionSeconds"
                    name={t('csInquiry.legendAvgResolution')}
                    stroke={CHART_COLORS[3]}
                    dot={false}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* メッセージ量推移 */}
          <Card className="p-4">
            <h3 className="mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
              {t('csInquiry.messageVolumeTitle')}
            </h3>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={data.dailySummary.map((d) => ({ ...d, label: d.date.slice(5) }))}
                  margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
                >
                  <CartesianGrid {...GRID_PROPS} />
                  <XAxis dataKey="label" tick={AXIS_TICK_STYLE} tickLine={false} />
                  <YAxis tick={AXIS_TICK_STYLE} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area
                    type="monotone"
                    dataKey="customerMessages"
                    name={t('csInquiry.legendCustomer')}
                    stackId="1"
                    stroke={CHART_COLORS[0]}
                    fill={CHART_COLORS[0]}
                    fillOpacity={0.3}
                  />
                  <Area
                    type="monotone"
                    dataKey="agentMessages"
                    name={t('csInquiry.legendAgent')}
                    stackId="1"
                    stroke={CHART_COLORS[1]}
                    fill={CHART_COLORS[1]}
                    fillOpacity={0.3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* 時間帯×曜日ヒートマップ */}
            <Card className="p-4">
              <h3 className="mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
                {t('csInquiry.heatmapTitle')}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      <th className="p-1 text-left text-[var(--color-text-muted)]" />
                      {Array.from({ length: 24 }, (_, h) => (
                        <th key={h} className="p-1 text-center text-[var(--color-text-muted)]">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {heatmapData.map((row) => (
                      <tr key={row.day}>
                        <td className="p-1 font-medium text-[var(--color-text-secondary)]">
                          {row.day}
                        </td>
                        {row.hours.map((val, h) => {
                          const opacity = maxHeatVal > 0 ? val / maxHeatVal : 0;
                          return (
                            <td key={h} className="p-0.5">
                              <div
                                className="mx-auto h-5 w-5 rounded-sm"
                                style={{
                                  backgroundColor: `rgba(0, 23, 193, ${String(0.05 + opacity * 0.85)})`,
                                }}
                                title={t('csInquiry.heatmapCell', {
                                  day: row.day,
                                  hour: String(h),
                                  num: String(val),
                                })}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* 担当者別統計 */}
            <Card className="p-4">
              <h3 className="mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
                {t('csInquiry.assigneeTitle')}
              </h3>
              {data.assigneeStats.length === 0 ? (
                <p className="text-sm text-[var(--color-text-muted)]">{t('common.noData')}</p>
              ) : (
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={data.assigneeStats.map((s) => ({
                        ...s,
                        name:
                          s.assigneeId === 'unassigned'
                            ? t('csInquiry.unassigned')
                            : s.assigneeId.slice(0, 8),
                      }))}
                      margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
                    >
                      <CartesianGrid {...GRID_PROPS} />
                      <XAxis dataKey="name" tick={AXIS_TICK_STYLE} tickLine={false} />
                      <YAxis tick={AXIS_TICK_STYLE} tickLine={false} axisLine={false} />
                      <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} />
                      <Bar
                        dataKey="chatCount"
                        name={t('csInquiry.legendChatCount')}
                        fill={CHART_COLORS[0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </div>

          <CsDailyDetailTable rows={data.dailySummary} />
        </>
      )}
    </div>
  );
}
